import * as React from 'react';
import { OTPInput, OTPInputContext, type OTPInputProps } from 'input-otp';
import { Dot } from 'lucide-react';

import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const inputOTPVariants = cva(
  'flex items-center has-[:disabled]:opacity-50 w-fit',
  {
    variants: {
      inputSize: {
        sm: 'gap-1',
        md: 'gap-2',
        lg: 'gap-3',
      },
    },
    defaultVariants: {
      inputSize: 'md',
    },
  },
);

const inputOTPGroupVariants = cva(
  'flex items-center border-2 border-neutral06 gap-3',
  {
    variants: {
      inputSize: {
        sm: 'rounded-lg',
        md: 'rounded-xl',
        lg: 'rounded-2xl p-6 py-[1.375rem]',
      },
    },
    defaultVariants: {
      inputSize: 'md',
    },
  },
);

/*
font-family: "TT Firs Neue";
font-size: 24px;
font-style: normal;
font-weight: 500;
line-height: 120%;

*/

const inputOTPSlotVariants = cva(
  `relative flex items-center justify-center transition-all
    first:rounded-l-[inherit] last:rounded-r-[inherit] font-medium
    after:absolute after:bottom-0 after:left-1/2 after:block after:h-[2px]
    after:bg-neutral05 after:transition-all after:w-0 after:-translate-x-1/2`,
  {
    variants: {
      inputSize: {
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'text-2xl size-9',
      },
    },
    defaultVariants: {
      inputSize: 'md',
    },
  },
);

type TInputOTPSize = 'sm' | 'md' | 'lg';

interface IInputOTPContextProps {
  inputSize: TInputOTPSize;
}

const InputOTPContext = React.createContext<IInputOTPContextProps>({
  inputSize: 'lg',
});

type TInputOTPProps = OTPInputProps &
  VariantProps<typeof inputOTPVariants> & {
    inputSize?: TInputOTPSize;
  };

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  TInputOTPProps
>(({ className, containerClassName, inputSize = 'lg', ...props }, ref) => (
  <InputOTPContext.Provider value={{ inputSize }}>
    <OTPInput
      ref={ref}
      containerClassName={cn(
        inputOTPVariants({ inputSize }),
        containerClassName,
      )}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  </InputOTPContext.Provider>
));
InputOTP.displayName = 'InputOTP';

interface IInputOTPGroupProps extends React.ComponentPropsWithoutRef<'div'> {}

const InputOTPGroup = React.forwardRef<
  React.ElementRef<'div'>,
  IInputOTPGroupProps
>(({ className, ...props }, ref) => {
  const { inputSize } = React.useContext(InputOTPContext);
  return (
    <div
      ref={ref}
      className={cn(inputOTPGroupVariants({ inputSize }), className)}
      {...props}
    />
  );
});
InputOTPGroup.displayName = 'InputOTPGroup';

interface IInputOTPSlotProps extends React.ComponentPropsWithoutRef<'div'> {
  index: number;
  value?: string;
  isActive?: boolean;
}

const InputOTPSlot = React.forwardRef<
  React.ElementRef<'div'>,
  IInputOTPSlotProps
>(({ index, value, isActive: isActiveOverride, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];
  const { inputSize } = React.useContext(InputOTPContext);
  const slotValue = value ?? char;
  const slotIsActive = isActiveOverride ?? isActive;

  return (
    <div
      ref={ref}
      className={cn(
        inputOTPSlotVariants({ inputSize }),
        { 'after:w-full': hasFakeCaret },
        { 'relative opacity-10': !slotIsActive && !slotValue },
        className,
      )}
      {...props}
    >
      {slotValue || '•'}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="bg-foreground h-4 w-px animate-caret-blink duration-1000" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = 'InputOTPSlot';

interface IInputOTPSeparatorProps extends React.ComponentPropsWithoutRef<'div'> {}

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<'div'>,
  IInputOTPSeparatorProps
>(({ ...props }, ref) => {
  const { inputSize } = React.useContext(InputOTPContext);
  return (
    <div
      ref={ref}
      role="separator"
      className={cn(
        inputSize === 'sm' && 'w-3',
        inputSize === 'md' && 'w-4',
        inputSize === 'lg' && 'w-5',
      )}
      {...props}
    >
      <Dot />
    </div>
  );
});
InputOTPSeparator.displayName = 'InputOTPSeparator';

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
